import { useState } from 'react'
import { useToast } from './toastContext'
import { feature as K } from './strings'
import { serverDetail } from '../lib/i18n'

/**
 * The page-level activation switch's behaviour, in one place.
 *
 * Four pages carry a `FeatureSwitch` and all four had written the same handler
 * by hand, which is how they drifted: three of them toasted only on FAILURE, so
 * turning surveys on looked identical to nothing happening, while the reports
 * page announced itself. Two were optimistic and one waited on the round trip,
 * so the switch also MOVED at different moments depending on which page you
 * were standing on.
 *
 * What it settles, once:
 *
 *   OPTIMISTIC, then put back. The switch moves on press, because a control
 *   that waits for a server before it visibly moves reads as broken; if the
 *   save fails it returns to where it was and the error says why.
 *
 *   THE SAME WORDS EVERYWHERE. Success uses the shared string — it is a state
 *   the UI asked for and already knows. A FAILURE prefers the server's own
 *   message, because only the server knows what went wrong; the shared string
 *   is the fallback for when it does not say.
 *
 *   ONE PRESS AT A TIME. `busy` swallows a second toggle while the first is in
 *   flight, so a double-click cannot leave the switch and the server disagreeing
 *   about which way it ended up.
 *
 * @param {{value: boolean, setValue: (next: boolean) => void,
 *          save: (next: boolean) => Promise<{ok: boolean, message?: string}>}} config
 *   `setValue` is a setter rather than a state key because the pages hold this
 *   flag differently — three in their own `useState`, one inside a `settings`
 *   object — and that is theirs to decide, not this hook's.
 */
export default function useFeatureToggle({ setValue, save }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  const toggle = async (next) => {
    if (busy) return
    setBusy(true)
    setValue(next)

    let outcome
    try {
      outcome = await save(next)
    } catch (error) {
      outcome = { ok: false, message: error?.message }
    }
    setBusy(false)

    if (!outcome?.ok) {
      setValue(!next)
      toast.error(serverDetail(outcome?.message) || K.failed)
      return
    }

    toast.success(next ? K.enabled : K.disabled)
  }

  return { toggle, busy }
}
